import { useState } from "react";
import {
  Box, Grid, TextField, Button, Typography,
  IconButton, MenuItem, ToggleButton, ToggleButtonGroup,
  Divider, FormControlLabel, Checkbox, Alert,
  Collapse, InputAdornment, Chip,
} from "@mui/material";
import CloseIcon            from "@mui/icons-material/Close";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import TwoWheelerIcon       from "@mui/icons-material/TwoWheeler";
import CurrencyRupeeIcon    from "@mui/icons-material/CurrencyRupee";
import api                  from "../service/api";

/* ─── Only engine, chasis, model are required ───────────────────────────── */
const REQUIRED_ALL = ["engine", "chasis", "model","salePrice","salePriceLead"];

const INIT = {
  vehicleType:        "E_RICKSHAW",
  date:               new Date().toISOString().split("T")[0],
  invoiceNo:          "",
  companyName:        "",
  model:              "",
  colour:             "",
  chasis:             "",
  engine:             "",
  location:           "",
   salePrice:          "",
salePriceLead:"",
  batteryType:        "",
  quantity:           "0",
  warrantyPeriod:     "",
  controllerNo:       "",
  controllerWarranty: "",
  motorWarranty:      "",
  charger:            "",
  chargerWarranty:    "",
  oamBattery:         false,
  form22Received:     false,
  HSN:                "",
  remarks:            "",
};

/* ─── Section header ────────────────────────────────────────────────────── */
const SectionTitle = ({ children, color = "#1565C0", bg = "#E3F2FD", chip }) => (
  <Box mb={1.5} mt={2.5}>
    <Box display="flex" alignItems="center" gap={1}>
      <Typography
        variant="caption" fontWeight={700}
        sx={{ color, bgcolor: bg, px: 1.5, py: 0.5, borderRadius: 2, fontSize: 11, letterSpacing: 0.5 }}
      >
        {children}
      </Typography>
      {chip && (
        <Chip
          label={chip} size="small"
          sx={{ bgcolor: "#FCE4EC", color: "#880E4F", fontSize: 10, height: 20 }}
        />
      )}
    </Box>
    <Divider sx={{ mt: 1 }} />
  </Box>
);

/* ─── F — field shorthand ───────────────────────────────────────────────── */
const F = ({
  name, label, type = "text", md = 6,
  prefix, select, children, required = false,
  form, errors, onChange,
}) => (
  <Grid item xs={12} md={md}>
    <TextField
      fullWidth size="small"
      name={name} label={label} type={type}
      value={form[name] ?? ""}
      onChange={onChange}
      error={!!errors[name]}
      helperText={errors[name] || ""}
      required={required}
      select={!!select}
      InputLabelProps={type === "date" ? { shrink: true } : undefined}
      InputProps={
        prefix
          ? { startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> }
          : undefined
      }
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
    >
      {select && children}
    </TextField>
  </Grid>
);

const rupee = <CurrencyRupeeIcon sx={{ fontSize: 14 }} />;

/* ═══════════════════════════════════════════════════════════════════════════ */
const CreateStockForm = ({ onSuccess, onClose }) => {
  const [form,     setForm]     = useState(INIT);
  const [errors,   setErrors]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [apiError, setApiError] = useState("");

  const isScooty = form.vehicleType === "SCOOTY";

  const fProps = { form, errors, onChange: handleChange };

  /* ── Validation — only engine, chasis, model required ────────────────── */
  function validate(values = form) {
    const errs = {};
    REQUIRED_ALL.forEach((key) => {
      const val = values[key];
      if (val === "" || val === null || val === undefined) {
        errs[key] = "Required";
      }
    });
     if (values.salePrice && isNaN(Number(values.salePrice)))
      errs.salePrice = "Must be a number";
    
    if (values.salePriceLead && isNaN(Number(values.salePriceLead)))
      errs.salePriceLead = "Must be a number";

    if (values.quantity && Number(values.quantity) < 0)
      errs.quantity = "Min 0";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  /* ── Change handler ───────────────────────────────────────────────────── */
  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    let val = type === "checkbox" ? checked : value;
    if (["engine", "chasis"].includes(name)) val = String(val).toUpperCase();
    const updated = { ...form, [name]: val };
    setForm(updated);
    if (errors[name]) {
      const newErrs = { ...errors };
      delete newErrs[name];
      setErrors(newErrs);
    }
  }

  /* ── Vehicle type toggle ──────────────────────────────────────────────── */
  const handleTypeChange = (_, newType) => {
    if (!newType) return;
    setForm({ ...form, vehicleType: newType });
    setErrors({});
  };

  /* ── Submit ───────────────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    setApiError("");
    if (!validate()) return;
    try {
      setLoading(true);
      await api.post("/create-stock", {
        vehicleType:        form.vehicleType,
        invoiceNo:          form.invoiceNo          || undefined,
        companyName:        form.companyName        || undefined,
        model:              form.model,
        colour:             form.colour             || undefined,
        chasis:             form.chasis,
        engine:             form.engine,
                salePrice:          form.salePrice    ? Number(form.salePrice)    : undefined,
salePriceLead:      form.salePriceLead ? Number(form.salePriceLead) : undefined,
        oamBattery:         form.oamBattery,
        batteryType:        form.batteryType        || undefined,
        quantity:           Number(form.quantity) || 0,
        warrantyPeriod:     form.warrantyPeriod     || undefined,
        form22Received:     form.form22Received,
        charger:            form.charger            || undefined,
        chargerWarranty:    form.chargerWarranty    || undefined,
        remarks:            form.remarks            || undefined,
        controllerNo:       form.controllerNo       || undefined,
        controllerWarranty: form.controllerWarranty || undefined,
        motorWarranty:      form.motorWarranty      || undefined,
        date:               form.date               || undefined,
        HSN:                form.HSN                || undefined,
        location:           form.location           || undefined,
      });
      onSuccess();
    } catch (err) {
      setApiError(
        err?.response?.data?.message || "Failed to create stock. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ── Submit button disabled until the 3 required fields are filled ──── */
  const isFormReady = REQUIRED_ALL.every(
    (k) => form[k] !== "" && form[k] !== null && form[k] !== undefined
  );

  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <Box p={3} position="relative" sx={{ maxHeight: "90vh", overflowY: "auto" }}>

      {/* Close button */}
      <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 12, right: 12 }}>
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Header */}
      <Typography variant="h6" fontWeight={700} mb={0.5}>Add EV stock</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Register a new electric vehicle into inventory
      </Typography>

      {/* ── Vehicle type toggle ─────────────────────────────────────────── */}
      <Box mb={2}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" mb={0.5} display="block">
          VEHICLE TYPE
        </Typography>
        <ToggleButtonGroup
          value={form.vehicleType} exclusive onChange={handleTypeChange}
          fullWidth size="small" sx={{ gap: 1 }}
        >
          <ToggleButton value="E_RICKSHAW" sx={{
            borderRadius: "8px !important", border: "1px solid !important", flex: 1, py: 1, gap: 1, fontWeight: 600,
            borderColor:  form.vehicleType === "E_RICKSHAW" ? "#E65100 !important" : "divider !important",
            bgcolor:      form.vehicleType === "E_RICKSHAW" ? "#FFF3E0 !important" : "transparent",
            color:        form.vehicleType === "E_RICKSHAW" ? "#E65100 !important" : "text.secondary",
          }}>
            <ElectricRickshawIcon fontSize="small" /> E-Rickshaw
          </ToggleButton>
          <ToggleButton value="SCOOTY" sx={{
            borderRadius: "8px !important", border: "1px solid !important", flex: 1, py: 1, gap: 1, fontWeight: 600,
            borderColor:  form.vehicleType === "SCOOTY" ? "#880E4F !important" : "divider !important",
            bgcolor:      form.vehicleType === "SCOOTY" ? "#FCE4EC !important" : "transparent",
            color:        form.vehicleType === "SCOOTY" ? "#880E4F !important" : "text.secondary",
          }}>
            <TwoWheelerIcon fontSize="small" /> Scooty
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* API error */}
      <Collapse in={!!apiError}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError("")}>
          {apiError}
        </Alert>
      </Collapse>

      {/* ── BASIC INFORMATION ───────────────────────────────────────────── */}
      <SectionTitle>Basic information</SectionTitle>
      <Grid container spacing={2}>
        <F name="invoiceNo"   label="Invoice no."               {...fProps} />
        <F name="date"        label="Date" type="date"          {...fProps} />
        <F name="companyName" label="Company name"              {...fProps} />
        <F name="HSN"         label="HSN code"                  {...fProps} />
        {/* model — REQUIRED */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth size="small" name="model" label="Model *" required
            value={form.model} onChange={handleChange}
            error={!!errors.model} helperText={errors.model || ""}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>
        <F name="colour" label="Colour" {...fProps} />

        {/* Engine — REQUIRED + auto uppercase */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth size="small" name="engine" label="Engine no. *" required
            value={form.engine} onChange={handleChange}
            error={!!errors.engine} helperText={errors.engine || "Auto-uppercase"}
            inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>

        {/* Chassis — REQUIRED + auto uppercase */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth size="small" name="chasis" label="Serial no. *" required
            value={form.chasis} onChange={handleChange}
            error={!!errors.chasis} helperText={errors.chasis || "Auto-uppercase"}
            inputProps={{ style: { textTransform: "uppercase", letterSpacing: 1 } }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Grid>

        {/* Location — optional */}
        <Grid item xs={12} md={6}>
          <TextField
            select fullWidth size="small"
            name="location" label="Location *"
            value={form.location} onChange={handleChange}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            <MenuItem value="Chaitanyapur">Chaitanyapur</MenuItem>
            <MenuItem value="Mahisadal">Mahisadal</MenuItem>
            <MenuItem value="Boroda">Boroda</MenuItem>
          </TextField>
        </Grid>
      </Grid>
 
    {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <SectionTitle color="#2E7D32" bg="#E8F5E9">Pricing</SectionTitle>
      <Grid container spacing={2}>
         <F name="salePrice"     label="Sale price(Lithium) *"     type="number" prefix={rupee} {...fProps} />
<F name="salePriceLead" label="Sale price (lead)" type="number" prefix={rupee} {...fProps} />
      </Grid>
      {/* ──  WARRANTY ──────────────────────────────────────────── */}
      <SectionTitle color="#6A1B9A" bg="#F3E5F5">  warranty</SectionTitle>
      <Grid container spacing={2}>

        {/* Battery type */}
        {/* <Grid item xs={12} md={6}>
          <TextField
            select fullWidth size="small"
            name="batteryType" label="Battery type"
            value={form.batteryType} onChange={handleChange}
            error={!!errors.batteryType} helperText={errors.batteryType || ""}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          >
            <MenuItem value="Lithium">Lithium</MenuItem>
            <MenuItem value="Lead">Lead</MenuItem>
          </TextField>
        </Grid> */}

        {/* <F name="quantity"           label="Quantity"                type="number" {...fProps} /> */}
        {/* <F name="warrantyPeriod"     label="Battery warranty expiry" type="date"   {...fProps} /> */}
        <F name="controllerNo"       label="Controller no."                        {...fProps} />
        <F name="controllerWarranty" label="Controller warranty"       {...fProps} />
        <F name="motorWarranty"      label="Motor warranty"           {...fProps} />

        {/* Checkboxes */}
        <Grid item xs={12} md={6}>
          <Box sx={{
            display: "flex", flexDirection: "column", gap: 0.5,
            p: 1.5, border: "1px solid", borderColor: "divider",
            borderRadius: 2, justifyContent: "center",
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small" name="oamBattery"
                  checked={form.oamBattery} onChange={handleChange}
                />
              }
              label={<Typography fontSize={13}>OAM battery included</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small" name="form22Received"
                  checked={form.form22Received} onChange={handleChange}
                />
              }
              label={<Typography fontSize={13}>Form 22 received</Typography>}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ── CHARGER ─────────────────────────────────────────────────────── */}
      {/* <SectionTitle
        color="#880E4F" bg="#FCE4EC"
        chip={isScooty ? undefined : undefined}
      >
        Charger details
      </SectionTitle>
      <Grid container spacing={2}>
        <F name="charger"         label="Charger serial / model"  {...fProps} />
        <F name="chargerWarranty" label="Charger warranty expiry" type="date" {...fProps} />
      </Grid> */}

      {/* ── REMARKS ─────────────────────────────────────────────────────── */}
      <SectionTitle color="#546E7A" bg="#ECEFF1">Remarks</SectionTitle>
      <TextField
        fullWidth multiline rows={3} size="small"
        name="remarks" label="Remarks"
        value={form.remarks} onChange={handleChange}
        error={!!errors.remarks} helperText={errors.remarks || ""}
        placeholder="Any additional observations, notes, or special conditions..."
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
      />

      {/* Validation summary */}
      {Object.keys(errors).length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please fill all required fields ({Object.keys(errors).length} missing)
        </Alert>
      )}

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <Box mt={3} display="flex" justifyContent="flex-end" gap={1.5}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormReady || loading}
          sx={{ minWidth: 140 }}
        >
          {loading ? "Creating…" : "Create stock"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateStockForm;